import React, { useMemo, useState } from "react";
import {
  Day,
  DragAndDrop,
  Inject,
  Resize,
  ScheduleComponent,
  TimelineViews,
  ViewDirective,
  ViewsDirective,
} from "@syncfusion/ej2-react-schedule";
import { IoMdAdd } from "react-icons/io";
import { GoChevronLeft, GoChevronRight, GoSearch } from "react-icons/go";
import { MdCalendarMonth, MdKeyboardArrowDown } from "react-icons/md";
import "./reservations-timeline.css";
import { reservationsMock, toScheduleEvents } from "./reservations.mock";
import "@syncfusion/ej2-base/styles/material.css";
import "@syncfusion/ej2-buttons/styles/material.css";
import "@syncfusion/ej2-calendars/styles/material.css";
import "@syncfusion/ej2-dropdowns/styles/material.css";
import "@syncfusion/ej2-inputs/styles/material.css";
import "@syncfusion/ej2-navigations/styles/material.css";
import "@syncfusion/ej2-popups/styles/material.css";
import "@syncfusion/ej2-react-schedule/styles/material.css";

const arabicDateFormatter = new Intl.DateTimeFormat("ar-SA", {
  day: "numeric",
  month: "long",
});

const isSameDay = (left, right) =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

function ReservationsTimeline() {
  const [selectedDate, setSelectedDate] = useState(new Date(2026, 1, 1));
  const [query, setQuery] = useState("");

  const filteredReservations = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const inSelectedDate = reservationsMock.filter((reservation) =>
      isSameDay(reservation.startTime, selectedDate)
    );

    if (!normalized) return inSelectedDate;

    return inSelectedDate.filter((reservation) => {
      const composed = [
        reservation.reservationNumber,
        reservation.tableLabel,
        `${reservation.guests}`,
        `حجز رقم #${reservation.reservationNumber}`,
      ]
        .join(" ")
        .toLowerCase();

      return composed.includes(normalized);
    });
  }, [query, selectedDate]);

  const events = useMemo(
    () => toScheduleEvents(filteredReservations),
    [filteredReservations]
  );

  const eventTemplate = (eventProps) => (
    <div className={`vip-reservation-event-wrap vip-status-${eventProps.status}`}>
      <div className="vip-reservation-event">
        <MdKeyboardArrowDown size={16} color="#525866" />
        <span className="vip-reservation-meta">{eventProps.tableLabel}</span>
        <span className="vip-reservation-subject">
          {`حجز رقم #${eventProps.reservationNumber}`}
        </span>
      </div>
    </div>
  );

  const dayLabel = arabicDateFormatter.format(selectedDate);

  return (
    <section className="vip-reservations-page">
      <header className="vip-reservations-toolbar">
        <div className="vip-search-wrap">
          <GoSearch className="vip-search-icon" size={16} />
          <input
            className="vip-search-input"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="بحث"
            type="text"
            value={query}
          />
        </div>

        <button className="vip-add-btn" type="button">
          <IoMdAdd size={20} />
          حجز جديد
        </button>

        <div className="vip-reservations-date-nav">
          <button
            aria-label="اليوم السابق"
            className="vip-pill-icon-btn"
            onClick={() =>
              setSelectedDate(
                (prevDate) =>
                  new Date(
                    prevDate.getFullYear(),
                    prevDate.getMonth(),
                    prevDate.getDate() - 1
                  )
              )
            }
            type="button"
          >
            <GoChevronRight size={20} />
          </button>
          <div className="vip-date-label">
            {dayLabel}
            <MdCalendarMonth size={20} />
          </div>
          <button
            aria-label="اليوم التالي"
            className="vip-pill-icon-btn"
            onClick={() =>
              setSelectedDate(
                (prevDate) =>
                  new Date(
                    prevDate.getFullYear(),
                    prevDate.getMonth(),
                    prevDate.getDate() + 1
                  )
              )
            }
            type="button"
          >
            <GoChevronLeft size={20} />
          </button>
        </div>
      </header>

      <div className="vip-calendar-shell">
        <ScheduleComponent
          cssClass="vip-reservations-schedule"
          currentView="TimelineDay"
          enableRtl={true}
          endHour="19:00"
          eventSettings={{ dataSource: events, template: eventTemplate }}
          height="560px"
          popupOpen={(args) => {
            args.cancel = true;
          }}
          readonly={false}
          rowAutoHeight={true}
          selectedDate={selectedDate}
          showHeaderBar={false}
          startHour="13:00"
          timeFormat="h:mm a"
          timeScale={{ enable: true, interval: 60, slotCount: 1 }}
        >
          <ViewsDirective>
            <ViewDirective option="TimelineDay" />
            <ViewDirective option="Day" />
          </ViewsDirective>
          <Inject services={[TimelineViews, Day, DragAndDrop, Resize]} />
        </ScheduleComponent>
      </div>
    </section>
  );
}

export default ReservationsTimeline;

